import React from "react";
import { Navigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Pencil, Trash2, Plus, Save, X, Image as ImageIcon, ExternalLink, Upload } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { useLang } from "../contexts/LangContext";
import { api, formatErrorDetail } from "../lib/api";
import ContentEditor from "../components/admin/ContentEditor";

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const { t } = useLang();
  const [tab, setTab] = React.useState("content");

  if (user === undefined) {
    return <div className="min-h-screen flex items-center justify-center text-white/60">Caricamento...</div>;
  }
  if (user === null) {
    return <Navigate to="/admin/login" replace />;
  }

  const tabs = [
    { key: "content", label: "Contenuti" },
    { key: "formats", label: t.admin.formats },
    { key: "events", label: t.admin.events },
    { key: "gallery", label: t.admin.gallery },
    { key: "settings", label: t.admin.settings },
    { key: "messages", label: t.admin.messages },
  ];

  return (
    <div className="min-h-screen bg-[#0a0a0a] pt-24 pb-24" data-testid="admin-dashboard">
      <div className="container-x">
        <div className="flex items-center justify-between gap-4 mb-10 flex-wrap">
          <div>
            <div className="eyebrow mb-2">{t.admin.dashboard}</div>
            <h1 className="h2 text-white">Pannello CMS</h1>
            <div className="text-[color:var(--txt-secondary)] mt-2 text-sm">
              {user.email}
            </div>
          </div>
          <div className="flex gap-3">
            <Link to="/" className="btn-ghost" data-testid="admin-view-site">
              <ExternalLink className="w-4 h-4" /> Vedi sito
            </Link>
            <button onClick={logout} className="btn-secondary" data-testid="admin-logout">
              <LogOut className="w-4 h-4" /> {t.admin.logout}
            </button>
          </div>
        </div>

        <div className="border-b border-white/10 flex flex-wrap gap-1 mb-10">
          {tabs.map((x) => (
            <button
              key={x.key}
              onClick={() => setTab(x.key)}
              className={`px-5 py-3 font-display text-xs tracking-[0.22em] uppercase transition-colors ${
                tab === x.key
                  ? "text-[color:var(--gold)] border-b-2 border-[color:var(--gold)]"
                  : "text-white/60 hover:text-white"
              }`}
              data-testid={`admin-tab-${x.key}`}
            >
              {x.label}
            </button>
          ))}
        </div>

        {tab === "content" && <ContentEditor />}
        {tab === "formats" && <FormatsAdmin />}
        {tab === "events" && <EventsAdmin />}
        {tab === "gallery" && <GalleryAdmin />}
        {tab === "settings" && <SettingsAdmin />}
        {tab === "messages" && <MessagesAdmin />}
      </div>
    </div>
  );
}

// -------------------- FORMATS --------------------
function FormatsAdmin() {
  const [items, setItems] = React.useState([]);
  const [editing, setEditing] = React.useState(null);

  const load = () => api.get("/formats?active_only=false").then((r) => setItems(r.data || []));
  React.useEffect(() => { load(); }, []);

  const blank = () => ({
    slug: "", title_it: "", title_en: "", tagline_it: "", tagline_en: "",
    description_it: "", description_en: "", tags: [], image_url: "", order: items.length + 1, is_active: true,
  });

  const save = async () => {
    if (!editing) return;
    try {
      const payload = { ...editing, tags: typeof editing.tags === "string" ? editing.tags.split(",").map((s) => s.trim()).filter(Boolean) : (editing.tags || []) };
      if (editing.id) {
        await api.patch(`/admin/formats/${editing.id}`, payload);
      } else {
        await api.post(`/admin/formats`, payload);
      }
      toast.success("Salvato");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatErrorDetail(err?.response?.data?.detail));
    }
  };

  const del = async (id) => {
    if (!window.confirm("Confermi l'eliminazione?")) return;
    await api.delete(`/admin/formats/${id}`);
    load();
  };

  return (
    <div data-testid="admin-formats">
      <ToolbarAdd onClick={() => setEditing(blank())} label="Nuovo format" />
      <ItemsTable
        items={items}
        columns={[
          { key: "title_it", label: "Titolo (IT)" },
          { key: "slug", label: "Slug" },
          { key: "order", label: "Ordine" },
          { key: "is_active", label: "Attivo", render: (v) => (v ? "✓" : "—") },
        ]}
        thumbKey="image_url"
        onEdit={(it) => setEditing({ ...it, tags: (it.tags || []).join(", ") })}
        onDelete={(it) => del(it.id)}
        testKey="format"
      />

      {editing && (
        <DrawerForm onClose={() => setEditing(null)} onSave={save} title={editing.id ? "Modifica format" : "Nuovo format"}>
          <Input label="Slug *" value={editing.slug} onChange={(v) => setEditing({ ...editing, slug: v })} testid="format-slug" />
          <Input label="Titolo (IT) *" value={editing.title_it} onChange={(v) => setEditing({ ...editing, title_it: v })} testid="format-title-it" />
          <Input label="Titolo (EN) *" value={editing.title_en} onChange={(v) => setEditing({ ...editing, title_en: v })} testid="format-title-en" />
          <Input label="Tagline (IT)" value={editing.tagline_it} onChange={(v) => setEditing({ ...editing, tagline_it: v })} />
          <Input label="Tagline (EN)" value={editing.tagline_en} onChange={(v) => setEditing({ ...editing, tagline_en: v })} />
          <Textarea label="Descrizione (IT)" value={editing.description_it} onChange={(v) => setEditing({ ...editing, description_it: v })} />
          <Textarea label="Descrizione (EN)" value={editing.description_en} onChange={(v) => setEditing({ ...editing, description_en: v })} />
          <Input label="Tags (separati da virgola)" value={editing.tags} onChange={(v) => setEditing({ ...editing, tags: v })} />
          <ImageUrlField label="URL immagine" value={editing.image_url} onChange={(v) => setEditing({ ...editing, image_url: v })} testid="format-image" />
          <Input type="number" label="Ordine" value={editing.order} onChange={(v) => setEditing({ ...editing, order: parseInt(v || "0", 10) })} />
          <Toggle label="Attivo" value={editing.is_active} onChange={(v) => setEditing({ ...editing, is_active: v })} />
        </DrawerForm>
      )}
    </div>
  );
}

// -------------------- EVENTS --------------------
function EventsAdmin() {
  const [items, setItems] = React.useState([]);
  const [editing, setEditing] = React.useState(null);
  const load = () => api.get("/events?active_only=false").then((r) => setItems(r.data || []));
  React.useEffect(() => { load(); }, []);
  const blank = () => ({
    title_it: "", title_en: "", category_it: "", category_en: "", description_it: "", description_en: "",
    location: "", year: "", image_url: "", order: items.length + 1, is_active: true,
  });
  const save = async () => {
    try {
      if (editing.id) await api.patch(`/admin/events/${editing.id}`, editing);
      else await api.post(`/admin/events`, editing);
      toast.success("Salvato"); setEditing(null); load();
    } catch (err) { toast.error(formatErrorDetail(err?.response?.data?.detail)); }
  };
  const del = async (id) => { if (window.confirm("Confermi?")) { await api.delete(`/admin/events/${id}`); load(); } };
  return (
    <div data-testid="admin-events">
      <ToolbarAdd onClick={() => setEditing(blank())} label="Nuovo evento" />
      <ItemsTable
        items={items}
        columns={[
          { key: "title_it", label: "Titolo (IT)" },
          { key: "category_it", label: "Categoria" },
          { key: "year", label: "Anno" },
          { key: "location", label: "Location" },
          { key: "is_active", label: "Attivo", render: (v) => (v ? "✓" : "—") },
        ]}
        thumbKey="image_url"
        onEdit={setEditing}
        onDelete={(it) => del(it.id)}
        testKey="event"
      />
      {editing && (
        <DrawerForm onClose={() => setEditing(null)} onSave={save} title={editing.id ? "Modifica evento" : "Nuovo evento"}>
          <Input label="Titolo (IT) *" value={editing.title_it} onChange={(v) => setEditing({ ...editing, title_it: v })} />
          <Input label="Titolo (EN) *" value={editing.title_en} onChange={(v) => setEditing({ ...editing, title_en: v })} />
          <Input label="Categoria (IT)" value={editing.category_it} onChange={(v) => setEditing({ ...editing, category_it: v })} />
          <Input label="Categoria (EN)" value={editing.category_en} onChange={(v) => setEditing({ ...editing, category_en: v })} />
          <Input label="Anno" value={editing.year} onChange={(v) => setEditing({ ...editing, year: v })} />
          <Input label="Location" value={editing.location} onChange={(v) => setEditing({ ...editing, location: v })} />
          <Textarea label="Descrizione (IT)" value={editing.description_it} onChange={(v) => setEditing({ ...editing, description_it: v })} />
          <Textarea label="Descrizione (EN)" value={editing.description_en} onChange={(v) => setEditing({ ...editing, description_en: v })} />
          <ImageUrlField label="URL immagine" value={editing.image_url} onChange={(v) => setEditing({ ...editing, image_url: v })} />
          <Input type="number" label="Ordine" value={editing.order} onChange={(v) => setEditing({ ...editing, order: parseInt(v || "0", 10) })} />
          <Toggle label="Attivo" value={editing.is_active} onChange={(v) => setEditing({ ...editing, is_active: v })} />
        </DrawerForm>
      )}
    </div>
  );
}

// -------------------- GALLERY --------------------
function GalleryAdmin() {
  const [items, setItems] = React.useState([]);
  const [editing, setEditing] = React.useState(null);
  const load = () => api.get("/gallery?active_only=false").then((r) => setItems(r.data || []));
  React.useEffect(() => { load(); }, []);
  const blank = () => ({ image_url: "", caption_it: "", caption_en: "", order: items.length + 1, is_active: true });
  const save = async () => {
    try {
      if (editing.id) await api.patch(`/admin/gallery/${editing.id}`, editing);
      else await api.post(`/admin/gallery`, editing);
      toast.success("Salvato"); setEditing(null); load();
    } catch (err) { toast.error(formatErrorDetail(err?.response?.data?.detail)); }
  };
  const del = async (id) => { if (window.confirm("Confermi?")) { await api.delete(`/admin/gallery/${id}`); load(); } };
  return (
    <div data-testid="admin-gallery">
      <ToolbarAdd onClick={() => setEditing(blank())} label="Nuova immagine" />
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
        {items.map((g) => (
          <div key={g.id} className="relative group border border-white/10" data-testid={`admin-gallery-item-${g.id}`}>
            <img src={g.image_url} alt="" className="w-full aspect-square object-cover" />
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/60 transition-colors flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
              <button onClick={() => setEditing(g)} className="btn-ghost !py-2 !px-3"><Pencil className="w-4 h-4" /></button>
              <button onClick={() => del(g.id)} className="btn-ghost !py-2 !px-3 hover:!border-[color:var(--fire)] hover:!text-[color:var(--fire)]"><Trash2 className="w-4 h-4" /></button>
            </div>
            {!g.is_active && <span className="absolute top-2 left-2 badge-pill bg-black/80">OFF</span>}
          </div>
        ))}
      </div>
      {editing && (
        <DrawerForm onClose={() => setEditing(null)} onSave={save} title={editing.id ? "Modifica immagine" : "Nuova immagine"}>
          <ImageUrlField label="URL immagine *" value={editing.image_url} onChange={(v) => setEditing({ ...editing, image_url: v })} />
          <Input label="Didascalia (IT)" value={editing.caption_it} onChange={(v) => setEditing({ ...editing, caption_it: v })} />
          <Input label="Didascalia (EN)" value={editing.caption_en} onChange={(v) => setEditing({ ...editing, caption_en: v })} />
          <Input type="number" label="Ordine" value={editing.order} onChange={(v) => setEditing({ ...editing, order: parseInt(v || "0", 10) })} />
          <Toggle label="Attivo" value={editing.is_active} onChange={(v) => setEditing({ ...editing, is_active: v })} />
        </DrawerForm>
      )}
    </div>
  );
}

// -------------------- SETTINGS --------------------
function SettingsAdmin() {
  const [settings, setSettings] = React.useState({});
  const [hero, setHero] = React.useState("");
  const [about, setAbout] = React.useState("");
  const [stats, setStats] = React.useState([]);

  const load = async () => {
    const { data } = await api.get("/settings");
    setSettings(data || {});
    setHero(data.hero_image || "");
    setAbout(data.about_image || "");
    setStats(data.stats || []);
  };
  React.useEffect(() => { load(); }, []);

  const saveKey = async (key, value) => {
    try {
      await api.put(`/admin/settings/${key}`, { value });
      toast.success("Salvato");
      load();
    } catch (err) { toast.error(formatErrorDetail(err?.response?.data?.detail)); }
  };

  return (
    <div className="space-y-10" data-testid="admin-settings">
      <div className="border border-white/10 p-7">
        <div className="eyebrow mb-4">Immagine Hero</div>
        <ImageUrlField label="URL hero image" value={hero} onChange={setHero} />
        <button onClick={() => saveKey("hero_image", hero)} className="btn-primary mt-5"><Save className="w-4 h-4" /> Salva</button>
      </div>
      <div className="border border-white/10 p-7">
        <div className="eyebrow mb-4">Immagine Chi Siamo</div>
        <ImageUrlField label="URL about image" value={about} onChange={setAbout} />
        <button onClick={() => saveKey("about_image", about)} className="btn-primary mt-5"><Save className="w-4 h-4" /> Salva</button>
      </div>
      <div className="border border-white/10 p-7">
        <div className="eyebrow mb-4">Numeri Impatto</div>
        <p className="text-sm text-[color:var(--txt-secondary)] mb-5">Modifica i 4 contatori della home (valore IT/EN, etichetta IT/EN).</p>
        <div className="space-y-4">
          {stats.map((s, i) => (
            <div key={i} className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <input className="field" placeholder="Valore IT" value={s.value_it} onChange={(e) => { const c=[...stats]; c[i]={...c[i], value_it:e.target.value}; setStats(c); }} />
              <input className="field" placeholder="Valore EN" value={s.value_en} onChange={(e) => { const c=[...stats]; c[i]={...c[i], value_en:e.target.value}; setStats(c); }} />
              <input className="field" placeholder="Etichetta IT" value={s.label_it} onChange={(e) => { const c=[...stats]; c[i]={...c[i], label_it:e.target.value}; setStats(c); }} />
              <input className="field" placeholder="Etichetta EN" value={s.label_en} onChange={(e) => { const c=[...stats]; c[i]={...c[i], label_en:e.target.value}; setStats(c); }} />
            </div>
          ))}
        </div>
        <button onClick={() => saveKey("stats", stats)} className="btn-primary mt-6"><Save className="w-4 h-4" /> Salva</button>
      </div>
    </div>
  );
}

// -------------------- MESSAGES --------------------
function MessagesAdmin() {
  const [items, setItems] = React.useState([]);
  const load = () => api.get("/admin/contact").then((r) => setItems(r.data || []));
  React.useEffect(() => { load(); }, []);
  const del = async (id) => { if (window.confirm("Eliminare la richiesta?")) { await api.delete(`/admin/contact/${id}`); load(); } };
  return (
    <div data-testid="admin-messages">
      {items.length === 0 && <div className="text-[color:var(--txt-secondary)] text-sm">Nessuna richiesta.</div>}
      <div className="space-y-4">
        {items.map((m) => (
          <div key={m.id} className="border border-white/10 p-6" data-testid={`admin-message-${m.id}`}>
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <div className="font-display text-xs tracking-[0.32em] uppercase text-[color:var(--gold)] mb-1">
                  {new Date(m.created_at).toLocaleString("it-IT")}
                </div>
                <div className="text-white text-lg font-display">{m.name} <span className="text-white/50">— {m.email}</span></div>
                <div className="text-[color:var(--txt-secondary)] text-sm mt-1">
                  {m.company} · {m.phone} · {m.event_type} · {m.event_date} · {m.participants} · {m.location}
                </div>
              </div>
              <button onClick={() => del(m.id)} className="btn-ghost hover:!border-[color:var(--fire)] hover:!text-[color:var(--fire)]">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
            <p className="text-white/85 mt-4 whitespace-pre-wrap">{m.message}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

// -------------------- HELPERS --------------------
function ToolbarAdd({ onClick, label }) {
  return (
    <div className="mb-6 flex">
      <button onClick={onClick} className="btn-primary" data-testid="admin-add-button">
        <Plus className="w-4 h-4" /> {label}
      </button>
    </div>
  );
}

function ItemsTable({ items, columns, onEdit, onDelete, thumbKey, testKey }) {
  return (
    <div className="border border-white/10 overflow-x-auto">
      <table className="w-full table-dark">
        <thead>
          <tr>
            {thumbKey && <th></th>}
            {columns.map((c) => <th key={c.key}>{c.label}</th>)}
            <th></th>
          </tr>
        </thead>
        <tbody>
          {items.map((it) => (
            <tr key={it.id} data-testid={`admin-${testKey}-row-${it.id}`}>
              {thumbKey && (
                <td className="w-20"><img src={it[thumbKey]} alt="" className="w-14 h-14 object-cover border border-white/10" /></td>
              )}
              {columns.map((c) => (
                <td key={c.key}>{c.render ? c.render(it[c.key], it) : it[c.key]}</td>
              ))}
              <td className="text-right whitespace-nowrap">
                <button onClick={() => onEdit(it)} className="text-white/70 hover:text-[color:var(--gold)] p-2"><Pencil className="w-4 h-4" /></button>
                <button onClick={() => onDelete(it)} className="text-white/70 hover:text-[color:var(--fire)] p-2"><Trash2 className="w-4 h-4" /></button>
              </td>
            </tr>
          ))}
          {items.length === 0 && (
            <tr><td colSpan={(columns?.length || 0) + 2} className="text-center py-10 text-[color:var(--txt-muted)]">Nessun elemento</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function DrawerForm({ children, onClose, onSave, title }) {
  return (
    <div className="fixed inset-0 z-[100] flex" data-testid="admin-drawer">
      <div className="flex-1 bg-black/70" onClick={onClose} />
      <div className="w-full max-w-xl h-full overflow-y-auto bg-[#0e0e0e] border-l border-white/10 p-8">
        <div className="flex items-center justify-between mb-8">
          <div className="font-display tracking-[0.18em] uppercase text-sm text-white">{title}</div>
          <button onClick={onClose} className="text-white/60 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <div className="space-y-5">{children}</div>
        <div className="flex gap-3 mt-10 pt-6 border-t border-white/10">
          <button onClick={onSave} className="btn-primary" data-testid="admin-drawer-save"><Save className="w-4 h-4" /> Salva</button>
          <button onClick={onClose} className="btn-ghost">Annulla</button>
        </div>
      </div>
    </div>
  );
}

function Input({ label, value, onChange, type = "text", testid }) {
  return (
    <label className="block">
      <span className="font-display text-[10px] tracking-[0.32em] uppercase text-[color:var(--gold)]">{label}</span>
      <input type={type} className="field mt-1" value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} />
    </label>
  );
}

function Textarea({ label, value, onChange }) {
  return (
    <label className="block">
      <span className="font-display text-[10px] tracking-[0.32em] uppercase text-[color:var(--gold)]">{label}</span>
      <textarea className="field mt-1" rows={3} value={value ?? ""} onChange={(e) => onChange(e.target.value)} />
    </label>
  );
}

function ImageUrlField({ label, value, onChange, testid }) {
  const fileInputRef = React.useRef(null);
  const [uploading, setUploading] = React.useState(false);

  const onPickFile = () => fileInputRef.current?.click();

  const onFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      toast.error("File troppo grande (max 8 MB)");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      // backend returns relative /api/uploads/... — convert to absolute URL
      const absolute = data.url.startsWith("http")
        ? data.url
        : `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
      onChange(absolute);
      toast.success("Immagine caricata");
    } catch (err) {
      toast.error(formatErrorDetail(err?.response?.data?.detail) || "Upload fallito");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  return (
    <div data-testid={testid ? `${testid}-wrap` : undefined}>
      <label className="block">
        <span className="font-display text-[10px] tracking-[0.32em] uppercase text-[color:var(--gold)]">{label}</span>
        <input
          className="field mt-1"
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder="https://...  oppure carica un file"
          data-testid={testid}
        />
      </label>

      <div className="mt-3 flex flex-wrap items-center gap-3">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif,image/avif"
          onChange={onFileChange}
          className="hidden"
          data-testid={testid ? `${testid}-file` : "image-file-input"}
        />
        <button
          type="button"
          onClick={onPickFile}
          disabled={uploading}
          className="btn-ghost !py-2 !px-3 disabled:opacity-60"
          data-testid={testid ? `${testid}-upload-btn` : "image-upload-btn"}
        >
          <Upload className="w-4 h-4" />
          {uploading ? "Caricamento..." : "Carica file"}
        </button>
        {value && (
          <button
            type="button"
            onClick={() => onChange("")}
            className="btn-ghost !py-2 !px-3 hover:!border-[color:var(--fire)] hover:!text-[color:var(--fire)]"
          >
            <X className="w-4 h-4" /> Rimuovi
          </button>
        )}
        <span className="text-[11px] text-[color:var(--txt-muted)]">JPG · PNG · WEBP · GIF · max 8 MB</span>
      </div>

      {value ? (
        <div className="mt-3 border border-white/10 aspect-[16/9] relative overflow-hidden">
          <img src={value} alt="" className="absolute inset-0 w-full h-full object-cover" />
        </div>
      ) : (
        <div className="mt-3 border border-dashed border-white/15 aspect-[16/9] flex items-center justify-center text-white/40 text-xs">
          <ImageIcon className="w-5 h-5 mr-2" /> Anteprima immagine
        </div>
      )}
    </div>
  );
}

function Toggle({ label, value, onChange }) {
  return (
    <label className="flex items-center justify-between border border-white/10 p-4">
      <span className="font-display text-xs tracking-[0.22em] uppercase text-white">{label}</span>
      <button
        type="button"
        onClick={() => onChange(!value)}
        className={`w-12 h-6 relative transition-colors ${value ? "bg-[color:var(--gold)]" : "bg-white/10"}`}
      >
        <span className={`absolute top-0.5 w-5 h-5 bg-black transition-all ${value ? "left-6" : "left-0.5"}`} />
      </button>
    </label>
  );
}
